import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { AlertCircle, ArrowLeft, CheckCircle, Fingerprint, Loader2, QrCode } from "lucide-react";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import useGeoLocation from "../../../CustomHooks/useGeoLocation";

const VIMO_WADH = "E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=";
const PRESETS = [100, 200, 500, 1000, 2000, 5000];
const API_BASE = process.env.REACT_APP_API_BASE_URL;


async function getDeviceFingerprint() {
  const raw = [
    navigator.userAgent,
    `${window.screen.width}x${window.screen.height}`,
    String(window.screen.colorDepth),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    navigator.platform,
    String(navigator.hardwareConcurrency),
  ].join("|");
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export default function VimoUPICashout({ onBack }) {
  const username = useSelector((s) => s.auth?.employeeDetails?.username);
  const token = useSelector((s) => s.auth?.employeeDetails?.token);
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const { location, fetchLocation } = useGeoLocation();
  const { captureFingerprint, loading: rdLoading, device } = useRDService();

  // ── APIs ──────────────────────────────────────────────────────────────
  const { callApi: statusApi } = useApi("/upicw/vimopay/onboarding-status", "GET");
  const { callApi: registerApi } = useApi("/upicw/vimopay/merchant/register", "POST");
  const { callApi: sendOtpApi } = useApi("/upicw/vimopay/otp/send", "POST");
  const { callApi: resendOtpApi } = useApi("/upicw/vimopay/otp/resend", "POST");
  const { callApi: validateOtpApi } = useApi("/upicw/vimopay/otp/validate", "POST");
  const { callApi: ekycApi } = useApi("/upicw/vimopay/ekyc", "POST");
  const { callApi: sellerOnboardApi } = useApi("/upicw/vimopay/seller/onboard", "POST");
  const { callApi: initiateApi } = useApi("/upicw/vimopay/initiate", "POST");
  const { callApi: statesApiCall } = useApi("/aeps/vimopay/states", "GET");
  const { callApi: districtsApiCall } = useApi("/aeps/vimopay/district", "POST");

  // ── Phase: "checking"|"register"|"otp"|"ekyc"|"seller"|"cashout"|"result"
  const [phase, setPhase] = useState("checking");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // Registration fields
  const [ip, setIp] = useState("");
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");

  // OTP
  const [otp, setOtp] = useState("");

  // Cashout
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [qrData, setQrData] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [txnResult, setTxnResult] = useState(null);

  const timerRef = useRef(null);
  const pollRef = useRef(null);

  // ── Init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchLocation();
    fetch("https://api.ipify.org?format=json")
      .then((r) => r.json())
      .then((d) => setIp(d.ip || ""))
      .catch(() => setIp(""));
    if (username) checkOnboardingStatus();
  }, [username]);

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(pollRef.current);
    };
  }, []);

  // Load states when register phase
  useEffect(() => {
    if (phase !== "register") return;
    setStatesLoading(true);
    statesApiCall(null)
      .then((res) => setStates(Array.isArray(res) ? res : []))
      .catch(() => setStates([]))
      .finally(() => setStatesLoading(false));
  }, [phase]);

  // Load districts when state changes
  useEffect(() => {
    if (!selectedState) { setDistricts([]); setSelectedDistrict(""); return; }
    setDistrictsLoading(true);
    setSelectedDistrict("");
    districtsApiCall({ stateCode: selectedState })
      .then((res) => setDistricts(Array.isArray(res) ? res : []))
      .catch(() => setDistricts([]))
      .finally(() => setDistrictsLoading(false));
  }, [selectedState]);

  const setMsg = (text, error = false) => { setMessage(text); setIsError(error); };

  // ── Onboarding status check ───────────────────────────────────────────
  const checkOnboardingStatus = async () => {
    try {
      setPhase("checking");
      const res = await statusApi(null, `mid=${username}`);
      const code = res?.responseCode;
      if (code === "READY") setPhase("cashout");
      else if (code === "SELLER_ONBOARDING_PENDING") setPhase("seller");
      else if (code === "EKYC_PENDING") setPhase("ekyc");
      else if (code === "OTP_PENDING") {
        setPhase("otp");
        sendOtpApi({ mid: username }).catch(() => { });
      } else {
        setPhase("register");
      }
    } catch {
      setPhase("register");
    }
  };

  // ── Step 1: Registration ──────────────────────────────────────────────
  const handleRegister = async () => {
    if (!fullName.trim()) { setMsg("Please enter your full name.", true); return; }
    if (mobile.length !== 10) { setMsg("Please enter a valid 10-digit mobile number.", true); return; }
    if (!selectedState) { setMsg("Please select a state.", true); return; }
    if (!selectedDistrict) { setMsg("Please select a district.", true); return; }
    try {
      setLoading(true);
      setMsg("");
      const res = await registerApi({
        mid: username,
        mobile,
        // latitude: location?.lat ? parseFloat(location.lat.toFixed(4)).toString() : "0",
        // longitude: location?.lng ? parseFloat(location.lng.toFixed(4)).toString() : "0",

        // TO THIS:
latitude: location?.lat ? location.lat.toFixed(4) : "0.0000",
longitude: location?.lng ? location.lng.toFixed(4) : "0.0000",

        ipAddress: ip,
        pipe: "3",
        merchantStateCode: selectedState,
        shopStateCode: selectedState,
        merchantDistrictCode: selectedDistrict,
        shopDistrictCode: selectedDistrict,
        fullName: fullName.trim(),
      });
      if (res?.status === "000") {
        setMsg("Registered! Sending OTP...");
        const otpRes = await sendOtpApi({ mid: username });
        if (otpRes?.status === "000") {
          setMsg("OTP sent to your registered mobile.");
          setPhase("otp");
        } else {
          setMsg(otpRes?.statusDescription || "OTP send failed. Please retry.", true);
        }
      } else {
        setMsg(res?.statusDescription || "Registration failed.", true);
      }
    } catch (e) {
      setMsg(e?.message || "Something went wrong.", true);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: OTP ───────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    try {
      setLoading(true); setMsg("");
      const res = await sendOtpApi({ mid: username });
      if (res?.status === "000") setMsg("OTP sent to your registered mobile.");
      else setMsg(res?.statusDescription || "Failed to send OTP.", true);
    } catch (e) { setMsg(e?.message || "Failed to send OTP.", true); }
    finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    try {
      setLoading(true); setMsg("");
      const res = await resendOtpApi({ mid: username });
      if (res?.status === "000") setMsg("OTP resent.");
      else setMsg(res?.statusDescription || "Failed to resend OTP.", true);
    } catch (e) { setMsg(e?.message || "Failed to resend OTP.", true); }
    finally { setLoading(false); }
  };

  const handleValidateOtp = async () => {
    if (otp.length < 4) { setMsg("Please enter the OTP.", true); return; }
    try {
      setLoading(true); setMsg("");
      const res = await validateOtpApi({ mid: username, otp });
      if (res?.status === "000") {
        setMsg("OTP verified!");
        setPhase("ekyc");
      } else {
        setMsg(res?.statusDescription || "Invalid OTP.", true);
      }
    } catch (e) { setMsg(e?.message || "OTP verification failed.", true); }
    finally { setLoading(false); }
  };

  // ── Step 3: eKYC ─────────────────────────────────────────────────────
  const handleEkyc = async () => {
    try {
      setLoading(true);
      setMsg("Place your finger on the scanner...");
      const pidXml = await captureFingerprint(VIMO_WADH);
      if (!pidXml || !pidXml.includes('errCode="0"')) {
        setMsg("Fingerprint capture failed. Please try again.", true);
        return;
      }
      setMsg("Submitting e-KYC...");
      const res = await ekycApi({ mid: username, pidData: pidXml });
      if (res?.status === "000") {
        setMsg("e-KYC completed!");
        setTimeout(() => { setMsg(""); setPhase("seller"); }, 1000);
      } else {
        setMsg(res?.statusDescription || "e-KYC failed.", true);
      }
    } catch (e) { setMsg(e?.message || "Something went wrong.", true); }
    finally { setLoading(false); }
  };

  // ── Step 4: Seller Onboarding ─────────────────────────────────────────
  const handleSellerOnboard = async () => {
    try {
      setLoading(true);
      setMsg("Processing activation...");
      const deviceFingerprint = await getDeviceFingerprint();
      const res = await sellerOnboardApi({
        mid: username,
        // latitude: location?.lat ? parseFloat(location.lat.toFixed(4)) : 0,
        // longitude: location?.lng ? parseFloat(location.lng.toFixed(4)) : 0,

        latitude: location?.lat ? location.lat.toFixed(4) : "0.0000",
longitude: location?.lng ? location.lng.toFixed(4) : "0.0000",

        ipAddress: ip,
        deviceFingerprint,
        appVersion: "1.8.0",
        osType: "WEB",
        osVersion: "13",
      });
      if (res?.responseCode === "000") {
        setMsg("Activation complete!");
        setTimeout(() => { setMsg(""); setPhase("cashout"); }, 1200);
      } else {
        setMsg(res?.message || res?.responseDescription || "Seller onboarding failed.", true);
      }
    } catch (e) { setMsg(e?.message || "Something went wrong.", true); }
    finally { setLoading(false); }
  };

  // ── Cashout: countdown ────────────────────────────────────────────────
  const startCountdown = (expiresAt) => {
    clearInterval(timerRef.current);
    const tick = () => {
      const secs = Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000));
      setCountdown(secs);
      if (secs <= 0) clearInterval(timerRef.current);
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  };

  // ── Cashout: poll status ──────────────────────────────────────────────
  const pollTxnStatus = (txnId) => {
    clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 24) { clearInterval(pollRef.current); return; }
      try {
        const res = await axios.get(`${API_BASE}/upicw/vimopay/status/${txnId}`, {
          headers: {
            Authorization: `Bearer ${tokenRef.current}`,
            "X-Channel": "WEB",
          },
        });
        const data = res?.data;
        if (data?.status === "SUCCESS" || data?.status === "FAILED") {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setTxnResult(data);
          setPhase("result");
        }
      } catch { /* silent */ }
    }, 5000);
  };

  // ── Cashout: initiate ─────────────────────────────────────────────────
  const handleInitiate = async () => {
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt < 100 || amt > 5000 || amt % 100 !== 0) {
      setMsg("Enter a valid amount (₹100–₹5,000 in multiples of ₹100).", true);
      return;
    }
    if (!location) { setMsg("Location access is required.", true); return; }
    try {
      setLoading(true); setMsg("");
      const deviceFingerprint = await getDeviceFingerprint();
      const res = await initiateApi({
        mid: username,
        amount: amt,
        // latitude: parseFloat(location.lat.toFixed(4)),
        // longitude: parseFloat(location.lng.toFixed(4)),
        latitude: location.lat.toFixed(4),
longitude: location.lng.toFixed(4),
        deviceFingerprint,
        appVersion: "1.8.0",
        osType: "WEB",
        osVersion: "13",
        remarks: remarks.trim() || "cash withdrawal",
      });
      if (res?.status === "PENDING" && res?.qrImageBase64) {
        setQrData(res);
        if (res.qrExpiresAt) startCountdown(res.qrExpiresAt);
        pollTxnStatus(res.txnId);
      } else {
        setMsg(res?.message || "Failed to generate QR.", true);
      }
    } catch (e) { setMsg(e?.message || "Something went wrong.", true); }
    finally { setLoading(false); }
  };

  const handleReset = () => {
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
    setQrData(null);
    setAmount("");
    setRemarks("");
    setCountdown(0);
    setMsg("");
  };

  // ── Shared UI ─────────────────────────────────────────────────────────
  const MsgBox = () =>
    message ? (
      <div className={`mt-4 p-4 rounded-xl text-sm text-center font-medium ${isError
        ? "bg-red-50 text-red-700 border border-red-200"
        : "bg-green-50 text-green-700 border border-green-200"
        }`}>
        {message}
      </div>
    ) : null;

  // ── Checking ──────────────────────────────────────────────────────────
  if (phase === "checking") {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#12345A]" />
        <span className="ml-3 text-slate-600 text-base">Checking onboarding status...</span>
      </div>
    );
  }

  // ── Result ────────────────────────────────────────────────────────────
  if (phase === "result" && txnResult) {
    const success = txnResult.status === "SUCCESS";
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 w-full max-w-md text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${success ? "bg-green-100" : "bg-red-100"
            }`}>
            {success
              ? <CheckCircle className="w-8 h-8 text-green-600" />
              : <AlertCircle className="w-8 h-8 text-red-600" />
            }
          </div>
          <h2 className={`text-xl font-bold mb-2 ${success ? "text-green-700" : "text-red-700"}`}>
            {success ? "Payment Received!" : "Transaction Failed"}
          </h2>
          {txnResult.amount && (
            <p className="text-3xl font-bold text-slate-800 my-3">₹{txnResult.amount}</p>
          )}
          {txnResult.txnId && (
            <p className="text-xs text-slate-400 font-mono mb-1">Txn: {txnResult.txnId}</p>
          )}
          {txnResult.message && (
            <p className="text-sm text-slate-500 mb-4">{txnResult.message}</p>
          )}
          <button
            onClick={() => { setTxnResult(null); setPhase("cashout"); }}
            className="w-full py-3 bg-[#12345A] text-white rounded-xl font-semibold"
          >
            New Transaction
          </button>
        </div>
      </div>
    );
  }

  // ── Cashout ───────────────────────────────────────────────────────────
  if (phase === "cashout") {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="w-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#12345A] flex items-center justify-center shrink-0">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800"> UPI Cash Withdrawal</h1>
              <p className="text-sm text-gray-500">Generate QR — customer pays, you hand over cash</p>
            </div>
          </div>

          {!qrData ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <p className="text-sm font-semibold text-slate-600 mb-3">Select or enter amount</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
                {PRESETS.map((p) => (
                  <button key={p} type="button" onClick={() => setAmount(String(p))}
                    className={`py-2 rounded-xl text-sm font-bold border transition-all ${amount === String(p)
                      ? "bg-[#12345A] text-white border-[#12345A]"
                      : "border-slate-200 text-slate-700 hover:border-[#12345A] hover:text-[#12345A]"
                      }`}>
                    ₹{p}
                  </button>
                ))}
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-slate-600 block mb-1.5">Amount (₹)</label>
                <input type="number" placeholder="Enter amount (multiples of ₹100)"
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  min={100} max={5000} step={100}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-lg font-bold text-slate-800 outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition" />
                <p className="text-xs text-gray-400 mt-1">₹100 – ₹5,000 · multiples of ₹100</p>
              </div>

              <div className="mb-5">
                <label className="text-sm font-medium text-slate-600 block mb-1.5">Remarks (optional)</label>
                <input type="text" placeholder="e.g. cash withdrawal" value={remarks} maxLength={100}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition" />
              </div>

              {!location && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl mb-4 text-xs text-amber-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  Location access is required. Please allow location in your browser.
                </div>
              )}

              {message && (
                <div className={`px-4 py-3 rounded-xl text-sm font-medium mb-4 ${isError
                  ? "bg-red-50 border border-red-200 text-red-700"
                  : "bg-green-50 border border-green-200 text-green-700"
                  }`}>{message}</div>
              )}

              <button onClick={handleInitiate} disabled={loading}
                className="w-full py-4 bg-[#12345A] hover:bg-[#0f2d50] text-white font-bold rounded-xl text-base transition disabled:opacity-50 flex items-center justify-center gap-2">
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating QR…</>
                  : <><QrCode className="w-5 h-5" /> Generate QR Code</>
                }
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="mb-4">
                <p className="text-xs text-gray-400 font-medium">Transaction ID</p>
                <p className="text-sm font-mono font-semibold text-slate-700">{qrData.txnId}</p>
              </div>

              <div className="flex flex-col items-center py-4">
                <img
                  src={`data:image/png;base64,${qrData.qrImageBase64}`}
                  alt="UPI QR Code"
                  className="w-56 h-56 sm:w-64 sm:h-64 object-contain rounded-xl border border-slate-100 shadow-sm"
                />
              </div>

              <div className={`mt-2 mb-4 text-center text-sm font-medium ${countdown > 0 ? "text-amber-600" : "text-gray-400"}`}>
                {countdown > 0 ? `QR expires in ${countdown}s` : "QR has expired"}
              </div>

              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl mb-4">
                <span className="text-sm text-gray-500 font-medium">Amount</span>
                <span className="text-xl font-bold text-[#12345A]">₹{Number(qrData.amount).toFixed(2)}</span>
              </div>

              <div className="text-center text-xs text-slate-400 mb-4 flex items-center justify-center gap-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Waiting for payment...
              </div>

              <button onClick={handleReset}
                className="w-full py-3 bg-[#12345A] hover:bg-[#0f2d50] text-white font-semibold rounded-xl transition text-sm">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Onboarding screens ────────────────────────────────────────────────
  const stepIndex = { register: 0, otp: 1, ekyc: 2, seller: 3 }[phase] ?? 0;
  const STEPS = ["Register", "OTP", "e-KYC", "Activate"];

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="w-full">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {onBack && (
            <button onClick={onBack} className="text-slate-500 hover:text-[#12345A] transition mr-1">
              <ArrowLeft size={20} />
            </button>
          )}
          <div className="w-12 h-12 rounded-2xl bg-[#12345A] flex items-center justify-center shrink-0">
            <QrCode className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800"> UPI Cashout Setup</h1>
            <p className="text-sm text-red-600 font-bold">One-time setup to activate UPI Cash Withdrawal</p>
          </div>
        </div>

        {/* Progress steps */}
        <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${i < stepIndex ? "bg-green-100 text-green-700"
                : i === stepIndex ? "bg-[#12345A] text-white"
                  : "bg-slate-100 text-slate-400"
                }`}>
                {i < stepIndex ? <CheckCircle className="w-3.5 h-3.5" /> : <span>{i + 1}</span>}
                <span className="ml-0.5">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200 min-w-3" />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">

          {/* ── Register ── */}
          {phase === "register" && (
            <>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Merchant Registration</h2>
              <p className="text-sm text-red-600 font-bold mb-4">
                Name should match PAN Card. Mobile should be linked to Aadhaar.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input type="text" placeholder="Enter full name as on PAN Card" value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition" />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input type="tel" inputMode="numeric" maxLength={10}
                  placeholder="Enter 10-digit mobile number" value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base font-semibold tracking-wider focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition" />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  State <span className="text-red-500">*</span>
                </label>
                <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)}
                  disabled={statesLoading}
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition bg-white disabled:opacity-60">
                  <option value="">{statesLoading ? "Loading..." : "Select state"}</option>
                  {states.map((s) => <option key={s.code} value={s.code}>{s.description}</option>)}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  District <span className="text-red-500">*</span>
                </label>
                <select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)}
                  disabled={!selectedState || districtsLoading}
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition bg-white disabled:opacity-60">
                  <option value="">
                    {!selectedState ? "Select a state first" : districtsLoading ? "Loading..." : "Select district"}
                  </option>
                  {districts.map((d) => <option key={d.code} value={d.code}>{d.description}</option>)}
                </select>
              </div>

              <button onClick={handleRegister}
                disabled={loading || !fullName.trim() || mobile.length !== 10 || !selectedState || !selectedDistrict}
                className="w-full bg-[#12345A] hover:bg-[#0F2E47] text-white py-4 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Registering..." : "Register & Get OTP"}
              </button>
            </>
          )}

          {/* ── OTP ── */}
          {phase === "otp" && (
            <>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Verify OTP</h2>
              <p className="text-sm text-slate-500 mb-5">OTP sent to your registered mobile number</p>

              <input type="text" inputMode="numeric" maxLength={6} value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter OTP"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-4 text-2xl tracking-[0.4em] text-center font-mono focus:outline-none focus:border-[#12345A] mb-4" />

              <button onClick={handleValidateOtp} disabled={loading || otp.length < 4}
                className="w-full bg-[#12345A] hover:bg-[#0F2E47] text-white py-4 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 mb-3">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button onClick={handleSendOtp} disabled={loading}
                  className="py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 disabled:opacity-50 text-sm">
                  Send OTP
                </button>
                <button onClick={handleResendOtp} disabled={loading}
                  className="py-3 border-2 border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50 disabled:opacity-50 text-sm">
                  Resend OTP
                </button>
              </div>
            </>
          )}

          {/* ── eKYC ── */}
          {phase === "ekyc" && (
            <>
              <div className="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-6">
                <Fingerprint size={50} className="text-indigo-600" />
              </div>

              <h2 className="text-2xl font-semibold text-center text-slate-800 mb-2">
                e-KYC Verification
              </h2>
              <p className="text-slate-500 text-center text-sm mb-8">
                Biometric authentication required for UPI Cash Withdrawal
              </p>

              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-sm text-slate-600 text-center">
                <p>Device: <strong className={device ? "text-green-600" : "text-red-500"}>{device || "Detecting..."}</strong></p>
                <p className="mt-1">Merchant: <strong>{username}</strong></p>
              </div>

              <button onClick={handleEkyc} disabled={loading || rdLoading}
                className="w-full bg-[#12345A] hover:bg-[#0F2E47] text-white py-4 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {(loading || rdLoading) && <Loader2 className="w-5 h-5 animate-spin" />}
                {loading || rdLoading ? "Capturing..." : "Capture Fingerprint for e-KYC"}
              </button>
            </>
          )}

          {/* ── Seller Onboarding ── */}
          {phase === "seller" && (
            <>
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={44} className="text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-800 text-center mb-1">Activate Account</h2>
              <p className="text-sm text-slate-500 text-center mb-6">
                Final step to activate your UPI Cashout account
              </p>

              <button onClick={handleSellerOnboard} disabled={loading}
                className="w-full bg-[#12345A] hover:bg-[#0F2E47] text-white py-4 rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Activating..." : "Activate UPI Cashout"}
              </button>
            </>
          )}

          <MsgBox />
        </div>
      </div>
    </div>
  );
}
